import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled, { useTheme } from 'styled-components';

import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogCoverLotsTransformed } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getItemSummaryCatalogId, getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';

type Props = {
    catalogId: number;
    catalogPage?: boolean;
    catalogUrl?: string;
    className?: string;
};

const CoverLotsWideComponent = (props: Props) => {
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const trackItemCardClicked = useTrackItemCardClicked();

    const mobile = !useIsWiderThan(tabletNarrowWidthNum);

    // the height is set at 300px for desktop and 125px for mobile
    const imageHeight = mobile ? 125 : 300;

    const { catalogId, className = '' } = props;
    const {
        primaryImage,
        primaryImageHighDpi,
        primaryItemId,
        primaryItemTitle,
        secondaryImage,
        secondaryImageHighDpi,
        secondaryItemId,
        secondaryItemTitle,
        tertiaryImage,
        tertiaryImageHighDpi,
        tertiaryItemId,
        tertiaryItemTitle,
    } = useAppSelector((state) => getCatalogCoverLotsTransformed(state, catalogId, imageHeight));

    const primaryItemUrl = useItemOrPriceResultLink(primaryItemId, true);
    const secondaryItemUrl = useItemOrPriceResultLink(secondaryItemId, true);
    const tertiaryItemUrl = useItemOrPriceResultLink(tertiaryItemId, true);

    const primaryCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, primaryItemId));
    const primaryPromoted = useAppSelector((state) => getItemSummaryPromoted(state, primaryItemId));
    const secondaryCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, secondaryItemId));
    const secondaryPromoted = useAppSelector((state) => getItemSummaryPromoted(state, secondaryItemId));
    const tertiaryCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, tertiaryItemId));
    const tertiaryPromoted = useAppSelector((state) => getItemSummaryPromoted(state, tertiaryItemId));

    return (
        <StyledCoverLotsWide className={className}>
            {primaryImage && (
                <StyledLink
                    onClick={() => {
                        trackItemCardClicked({
                            catalogId: primaryCatalogId,
                            itemId: primaryItemId,
                            promoted: primaryPromoted,
                            source: ItemCardSource.CoverLots,
                        });
                    }}
                    route
                    to={primaryItemUrl}
                >
                    <StyledImage
                        alt={primaryItemTitle}
                        height={imageHeight}
                        src={primaryImage}
                        srcSet={`${primaryImageHighDpi} 2x`}
                        width={imageHeight}
                    />
                </StyledLink>
            )}
            {secondaryImage && (
                <StyledLink
                    onClick={() => {
                        trackItemCardClicked({
                            catalogId: secondaryCatalogId,
                            itemId: secondaryItemId,
                            promoted: secondaryPromoted,
                            source: ItemCardSource.CoverLots,
                        });
                    }}
                    route
                    to={secondaryItemUrl}
                >
                    <StyledImage
                        alt={secondaryItemTitle}
                        height={imageHeight}
                        src={secondaryImage}
                        srcSet={`${secondaryImageHighDpi} 2x`}
                        width={imageHeight}
                    />
                </StyledLink>
            )}
            {tertiaryImage && (
                <StyledLink
                    onClick={() => {
                        trackItemCardClicked({
                            catalogId: tertiaryCatalogId,
                            itemId: tertiaryItemId,
                            promoted: tertiaryPromoted,
                            source: ItemCardSource.CoverLots,
                        });
                    }}
                    route
                    to={tertiaryItemUrl}
                >
                    <StyledImage
                        alt={tertiaryItemTitle}
                        height={imageHeight}
                        src={tertiaryImage}
                        srcSet={`${tertiaryImageHighDpi} 2x`}
                        width={imageHeight}
                    />
                </StyledLink>
            )}
        </StyledCoverLotsWide>
    );
};

export default CoverLotsWideComponent;

const StyledImage = styled.img`
    max-height: 100%;
    max-width: 100%;
    z-index: 10;
    object-fit: contain;
`;

const StyledLink = styled(Link)`
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    flex: 1 1 calc(100% / 3);
    padding: 25px;

    &:nth-child(2) {
        padding: 25px 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: 4px;

        &:nth-child(2) {
            padding: 4px 0;
        }
    }
`;

const StyledCoverLotsWide = styled.div`
    height: 300px;
    width: 100%;
    max-height: 300px;
    position: relative;
    display: flex;
    flex-direction: row;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        max-height: 125px;
    }
`;
